import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatRelativeTime(dateString) {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function extractTitleFromUrl(url) {
  if (!url) return 'Untitled Product';
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    const parts = path.split('/').filter(Boolean);
    const lastPart = parts[parts.length - 1] || '';
    return lastPart
      .replace(/[-_]/g, ' ')
      .replace(/\.(html|php|aspx)$/i, '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ') || 'Product';
  } catch {
    return 'Product';
  }
}

export function getFirstImage(product) {
  if (product?.image_urls && Array.isArray(product.image_urls) && product.image_urls.length > 0) {
    return product.image_urls[0];
  }
  return null;
}

export function calculateOverallProgress(product, relatedData) {
  const { reviews, marketing, ugc, winning, winningImages, staticAds, shopify } = relatedData || {};
  
  let total = 0;
  let completed = 0;

  // Product details (20%)
  total += 20;
  if (product?.product_status === 'Product Details Completed') completed += 20;
  else if (product?.product_status === 'Product Details in Progress') completed += 10;

  // Reviews (15%)
  total += 15;
  if (reviews?.review_status === 'Reviews Completed') completed += 15;
  else if (reviews?.review_status === 'Reviews In Progress') completed += 7;

  // Marketing angles (15%)
  total += 15;
  if (marketing?.status === 'Completed') completed += 15;
  else if (marketing?.status === 'In Progress') completed += 7;

  // UGC (20%)
  total += 20;
  if (ugc?.status === 'Completed') completed += 20;
  else if (ugc?.full_video_status === 'Completed' && ugc?.shorts_status === 'Completed') completed += 18;
  else if (ugc?.status === 'In Progress') completed += 10;

  // Winning product (15%)
  total += 15;
  if (winning?.status === 'Completed' && winningImages?.status === 'Completed') completed += 15;
  else if (winning?.status === 'Completed') completed += 10;
  else if (winning?.status === 'In Progress') completed += 5;

  // Static ads (10%)
  total += 10;
  if (staticAds?.status === 'Generation Complete') completed += 10;
  else if (staticAds?.status?.includes('In Progress')) completed += 5;

  // Shopify (5%)
  total += 5;
  if (shopify?.status === 'Completed') completed += 5;
  else if (shopify?.status === 'In Progress') completed += 2;

  return total > 0 ? Math.round((completed / total) * 100) : 0;
}

export function calculateLaneProgress(product, relatedData, lane) {
  const { reviews, marketing, ugc, winning, winningImages, staticAds, shopify } = relatedData || {};

  switch (lane) {
    case 'research':
      let researchTotal = 0;
      let researchCompleted = 0;
      // Product details
      researchTotal += 40;
      if (product?.product_status === 'Product Details Completed') researchCompleted += 40;
      else if (product?.product_status === 'Product Details in Progress') researchCompleted += 20;
      // Reviews
      researchTotal += 30;
      if (reviews?.review_status === 'Reviews Completed') researchCompleted += 30;
      else if (reviews?.review_status === 'Reviews In Progress') researchCompleted += 15;
      // Marketing angles
      researchTotal += 30;
      if (marketing?.status === 'Completed') researchCompleted += 30;
      else if (marketing?.status === 'In Progress') researchCompleted += 15;
      return researchTotal > 0 ? Math.round((researchCompleted / researchTotal) * 100) : 0;

    case 'ugc':
      if (!ugc) return 0;
      if (ugc.status === 'Completed') return 100;
      let ugcProgress = 0;
      if (ugc.full_video_status === 'Completed') ugcProgress += 50;
      else if (ugc.full_video_status === 'In Progress') ugcProgress += 25;
      if (ugc.shorts_status === 'Completed') ugcProgress += 50;
      else if (ugc.shorts_status === 'In Progress') ugcProgress += 25;
      return ugcProgress;

    case 'winning':
      let winningTotal = 0;
      let winningCompleted = 0;
      winningTotal += 60;
      if (winning?.status === 'Completed') winningCompleted += 60;
      else if (winning?.status === 'In Progress') winningCompleted += 30;
      winningTotal += 40;
      if (winningImages?.status === 'Completed') winningCompleted += 40;
      else if (winningImages?.status === 'In Progress') winningCompleted += 20;
      return winningTotal > 0 ? Math.round((winningCompleted / winningTotal) * 100) : 0;

    case 'ads':
      if (!staticAds) return 0;
      if (staticAds.status === 'Generation Complete') return 100;
      if (staticAds.status === 'Prompts Pending') return 0;
      if (staticAds.status === 'Generation Pending') return 10;
      if (staticAds.status === 'Generation In Progress') return 20;
      // Calculate based on ad progress
      const adStatuses = ['Ad2', 'Ad3', 'Ad4', 'Ad5', 'Ad6', 'Ad7', 'Ad8'];
      const currentAdIndex = adStatuses.findIndex(ad => staticAds.status?.includes(ad));
      if (currentAdIndex >= 0) return 20 + (currentAdIndex + 1) * 10;
      return 50;

    case 'shopify':
      if (!shopify) return 0;
      if (shopify.status === 'Completed') return 100;
      if (shopify.status === 'In Progress') return 50;
      if (shopify.status === 'Images Uploaded') return 70;
      return 0;

    default:
      return 0;
  }
}

export function getStatusColor(status) {
  if (!status) return { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300' };
  
  const statusLower = status.toLowerCase();
  
  if (statusLower.includes('completed') || statusLower.includes('complete') || statusLower.includes('ready')) {
    return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
  }
  if (statusLower.includes('progress') || statusLower.includes('in progress')) {
    return { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' };
  }
  if (statusLower.includes('failed') || statusLower.includes('error')) {
    return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
  }
  if (statusLower.includes('pending') || statusLower.includes('waiting')) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
  }
  
  return { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300' };
}

export function getPlatformIcon(platform) {
  switch (platform?.toLowerCase()) {
    case 'amazon':
      return '🛒';
    case 'shopify':
      return '🛍️';
    case 'etsy':
      return '🎨';
    default:
      return '📦';
  }
}
